import * as crypto from 'crypto';
import { Equipment, EquipmentType, isEquipmentIdTemplate, isEquipmentValid } from '@cf-shared/data-model/equipment';
import { DateTime } from 'luxon';
import { FitnessState, ExerciseStat, uniqueifyEquipmentName } from '@cf-shared/data-model/user-state';
import { deepClone } from '@cf-shared/deep-helpers';
import { LocaleSettings } from '@cf-shared/shared-util';
import { getMeasureUnitConversion } from 'util/formatting';
import { LL } from 'i18n/i18n.service';
import {
  equipmentIdForUnit, equipmentUnitOrKg, isCollisionDamage, legacyEquipmentId, legacyEquipmentIdUsableForUnit, LegacyEquipmentMigration,
  migrateLegacyEquipmentId,
} from './exercise-stat-equipment';

// the timestamp alone repeats within one millisecond, and preselect creates a kg copy and an
// lb copy back to back — so random bytes go in too, or the second copy overwrites the first
// (df-80e9963022e5). The output keeps its old shape: a base64 SHA1.
export function generateEquipmentId(): string {
  return crypto
    .createHmac('SHA1', 'PA45GBRvXQKu7F7GzZa')
    .update(DateTime.utc().toISO())
    .update(crypto.randomBytes(16))
    .digest('base64');
}

export function getUnitWithLocaleFallback(weightUnit: string | undefined, locale: LocaleSettings, defaultUnit?: string): string {
  if (weightUnit) return weightUnit;
  if (defaultUnit) return defaultUnit;
  const { unitName } = getMeasureUnitConversion('addedweight_kg', locale, undefined);
  return unitName;
}

export function getEquipmentForExercise(fitnessState: FitnessState, stat: ExerciseStat, locale: LocaleSettings): Equipment {
  const unitKey = getUnitWithLocaleFallback(stat?.weight_unit, locale);

  // if migrating legacy equipmentId to equipmentIdByUnit
  migrateLegacyEquipmentId(stat, unitKey, LegacyEquipmentMigration.CopyWhenNoSlotsKeepingLegacy);

  // if we have unit-specific equipment
  const unitEquipmentId = equipmentIdForUnit(stat, unitKey);
  const unitEquipment = unitEquipmentId && fitnessState.allEquipment?.[unitEquipmentId];
  // if both unit slots hold the same equipment and it is of the OTHER unit, this is the damage
  // two copies sharing one id left behind (df-80e9963022e5): treat the slot as empty, so the
  // workout screen re-preselects and its #2876 guard clears it. A machine the person switched
  // to the other unit on the edit page sits in one slot only, and is still used as before
  if (unitEquipment && !isCollisionDamage(stat, unitKey, unitEquipmentId, unitEquipment)) {
    return unitEquipment;
  }

  // fall back to legacy equipmentId field for backward compatibility, unless it names a machine
  // configured in the other unit (df-0caf8c4d43d4): then give none, so the workout screen
  // preselects a machine for this unit under its #2876 rule
  const legacyId = legacyEquipmentIdUsableForUnit(stat, unitKey, fitnessState.allEquipment);
  if (legacyId && fitnessState.allEquipment && fitnessState.allEquipment[legacyId]) {
    return fitnessState.allEquipment[legacyId];
  }
  return undefined;
}

/**
 * Where the machine for a weight unit came from when somebody changes the unit of an
 * exercise (#5345). The workout screen acts differently on each, so the decision and
 * what it acted on are returned together rather than as a bare machine.
 */
export enum UnitSwitchEquipmentSource {
  // a machine is already recorded for the unit being switched TO — nothing to decide
  RecordedForUnit = 'recorded_for_unit',
  // nothing is recorded for the new unit, but the other unit has a usable machine, so
  // the app copies that one (same physical positions, shown in the new unit)
  CopiedFromOtherUnit = 'copied_from_other_unit',
  // neither unit has a machine this exercise can use
  None = 'none',
}

export interface UnitSwitchEquipmentDecision {
  source: UnitSwitchEquipmentSource;
  // the machine to use (RecordedForUnit) or the one to copy (CopiedFromOtherUnit)
  equipment?: Equipment;
}

/**
 * #5345: which machine an exercise should use after its weight unit changed.
 *
 * Before this, changing the unit mid-workout with no machine recorded for the new unit
 * left the OTHER unit's machine silently attached to the screen. The next weight the
 * person entered was then measured against a machine configured in the other unit, so an
 * ordinary round number read as off-grid and concluding the set threw the machine away
 * and invented a temporary one. The full story, with the reporter's own numbers, is in
 * `design-docs/reactive-deload.md`, the #5325 section.
 *
 * Pure: it reads state and decides, it writes nothing. The caller applies the decision.
 *
 * Every candidate is checked against `allEquipment` and `isEquipmentValid` rather than
 * trusted, because a stat read from the database may point at a machine that was deleted
 * or was written by an older version of the app.
 */
export function decideEquipmentForUnitSwitch(
  fitnessState: FitnessState,
  exerciseStat: ExerciseStat,
  newUnit: 'kg' | 'lb',
): UnitSwitchEquipmentDecision {
  const usableEquipment = (equipmentId: string | undefined): Equipment | undefined => {
    if (!equipmentId) {
      return undefined;
    }
    const equipment = fitnessState?.allEquipment?.[equipmentId];
    return isEquipmentValid(equipment) ? equipment : undefined;
  };

  const otherUnit = newUnit === 'lb' ? 'kg' : 'lb';

  // a machine counts as recorded FOR a unit only when the machine itself is configured in that
  // unit. The key it is filed under is not enough: the legacy migration in
  // WorkoutDetailPage.preselectEquipmentIfNeeded files the old single machine under the stat's
  // DISPLAY unit, so somebody whose stat says kilograms while their only machine is the pounds
  // one ends up with a pounds machine under the kg key. Trusting that key would hand this
  // exercise back the wrong-unit machine, which is the state this whole decision exists to end,
  // and it would leave the #2876 rule broken in the stored document. Falling through to the copy
  // below repairs it instead.
  const machineForUnit = (equipmentId: string | undefined): Equipment | undefined => {
    const equipment = usableEquipment(equipmentId);
    if (!equipment) {
      return undefined;
    }
    // a machine that never recorded a unit counts as kilograms, the same reading every other
    // place in this area applies. Demanding an exact match instead would treat such a machine as
    // belonging to the other unit and copy it needlessly, telling the person the app carried over
    // a machine they never had.
    return equipmentUnitOrKg(equipment) === newUnit ? equipment : undefined;
  };

  const recordedForNewUnit = machineForUnit(equipmentIdForUnit(exerciseStat, newUnit))
    // the legacy single-machine field, from before equipment was recorded per unit
    || machineForUnit(legacyEquipmentId(exerciseStat));
  if (recordedForNewUnit) {
    return { source: UnitSwitchEquipmentSource.RecordedForUnit, equipment: recordedForNewUnit };
  }

  const recordedForOtherUnit = usableEquipment(equipmentIdForUnit(exerciseStat, newUnit))
    || usableEquipment(equipmentIdForUnit(exerciseStat, otherUnit))
    || usableEquipment(legacyEquipmentId(exerciseStat));
  if (recordedForOtherUnit) {
    return { source: UnitSwitchEquipmentSource.CopiedFromOtherUnit, equipment: recordedForOtherUnit };
  }

  return { source: UnitSwitchEquipmentSource.None };
}

/**
 * #5345: make a copy of a machine that is shown in the other weight unit.
 *
 * Every weight on a machine is stored in kilograms already — stack positions, plates, bar,
 * step size — so nothing is recalculated. The copy holds the same physical positions and
 * only says which unit to display them in. That is the point: the machine in the gym did
 * not change because the display did.
 *
 * The copy is a separate machine with its own id, registered in `fitnessState.allEquipment`
 * by `duplicateEquipment`, so the exercise can record one machine per unit and the
 * "a machine's own unit matches the key it is stored under" rule (#2876) still holds.
 */
export function copyEquipmentToOtherUnit(fitnessState: FitnessState, source: Equipment, targetUnit: 'kg' | 'lb'): Equipment {
  // duplicateEquipment names the copy from the unit the equipment carries, so set the unit
  // on a detached clone first and duplicate that
  const sourceInTargetUnit = deepClone(source);
  sourceInTargetUnit.weight_unit = targetUnit;
  return duplicateEquipment(fitnessState, sourceInTargetUnit, true);
}

export function duplicateEquipment(fitnessState: FitnessState, original: Equipment, forAlternateUnit?: boolean): Equipment {
  console.assert(original.type !== EquipmentType.Unknown);
  let newEquipment = deepClone(original);

  // if the original is a template, wrap the name in "My ..." to indicate to the user that they
  // created this. it is one translated phrase, not a glued-on prefix, because other languages
  // put the possessive somewhere else in the phrase (#4210 Phase E)
  let baseName = isEquipmentIdTemplate(original.id)
    ? LL.equipment.labels.myEquipmentName({ name: newEquipment.name })
    : newEquipment.name;
  
  // For dual equipment creation (equipment for the alternate unit), use [lb] or [kg] suffix
  if (forAlternateUnit) {
    // Remove any existing [lb] or [kg] suffix first
    baseName = baseName.replace(/\s*\[(lb|kg)\]\s*$/, '');
    // Add the appropriate suffix based on the new equipment's unit
    const suffix = newEquipment.weight_unit === 'lb' ? '[lb]' : '[kg]';
    newEquipment.name = uniqueifyEquipmentName(fitnessState, `${baseName} ${suffix}`);
  } else {
    // create a unique name with number suffix as usual
    newEquipment.name = uniqueifyEquipmentName(fitnessState, baseName);
  }
  
  // while the id is already taken, draw another, so a new copy can never overwrite existing equipment
  do {
    newEquipment.id = generateEquipmentId();
  } while (fitnessState.allEquipment[newEquipment.id]);

  // add it to the list of equipment
  fitnessState.allEquipment[newEquipment.id] = newEquipment;

  return newEquipment;
}
